import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import ChecklistIcon from '@mui/icons-material/Checklist'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import moment from 'moment'
import API from '../../../services/API'
import { GlobalContext, UserContext } from '../../../App'
import { SEVERITY } from '../makeReady/severity'
import LoadListSelect from './LoadListSelect'
import useLoadLists, { loadListsForKind } from './useLoadLists'
import { canManageMakeReady, useMakeReadyTierAllowed } from './makeReadyAccess'

const describeCheck = (status) => {
  if (!status.lastCheckedAt) return 'Never checked'
  const parts = [`Checked ${moment(status.lastCheckedAt).fromNow()}`]
  if (status.missingCount > 0) parts.push(`${status.missingCount} missing`)
  if (status.expiredCount > 0) parts.push(`${status.expiredCount} expired`)
  return parts.join(' · ')
}

// Make Ready summary for one asset, used in the Asset Manager drawer.
// Fetches /makeready/status/:id itself (ignoring stale responses when the
// drawer switches asset) and lets managers assign or change the load list
// in place. Hidden entirely below tier 2, matching the module's gate.
const AssetMakeReadyBlock = ({ assetId, onChanged }) => {
  const navigate = useNavigate()
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const allowed = useMakeReadyTierAllowed(2)
  const canManage = canManageMakeReady(user)

  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [selected, setSelected] = useState('')
  const [saving, setSaving] = useState(false)

  const { loadLists } = useLoadLists({ enabled: allowed && !!assetId })

  useEffect(() => {
    if (!allowed || !assetId) return undefined
    let active = true
    setStatus(null)
    setEditing(false)
    setLoading(true)
    API.get(`/makeready/status/${assetId}`)
      .then((res) => {
        if (!active) return
        if (res.status === 200) {
          setStatus(res.data)
          setSelected(
            res.data.loadList?._id || res.data.suggestedLoadList?._id || '',
          )
        } else {
          setStatus({ error: true })
        }
      })
      .catch(() => {
        if (active) setStatus({ error: true })
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [assetId, allowed])

  // Untracked asset with exactly one candidate list: pre-select it.
  useEffect(() => {
    if (selected || !status || status.tracked) return
    const options = loadListsForKind(loadLists, 'asset')
    if (options.length === 1) setSelected(options[0]._id)
  }, [loadLists, status, selected])

  if (!allowed || !assetId) return null

  const assign = ({ runCheck }) => {
    if (!selected || saving) return
    setSaving(true)
    API.post('/makeready/assign-load-list', {
      targetId: assetId,
      loadListId: selected,
    })
      .then((res) => {
        if (res.status === 200) {
          onChanged?.(res.data?.loadList || null)
          if (runCheck) {
            navigate(`/make-ready/${assetId}`)
            return
          }
          setToast({ message: 'Load list updated' })
          setEditing(false)
          setStatus((prev) => ({
            ...prev,
            tracked: true,
            loadList: res.data?.loadList || prev?.loadList,
            level: prev?.lastCheckedAt ? prev.level : 'neverChecked',
          }))
        } else {
          setToast({
            message:
              res.data?.error ||
              'Unable to assign that load list. Please try again.',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to assign that load list. Please try again.',
          severity: 'error',
        }),
      )
      .finally(() => setSaving(false))
  }

  const untracked = !!status && !status.error && !status.tracked
  const level = status?.level && SEVERITY[status.level] ? status.level : null
  const severity = level ? SEVERITY[level] : null

  return (
    <Box
      sx={{
        border: `1px ${untracked ? 'dashed #94A3B8' : 'solid #E2E8F0'}`,
        bgcolor: '#F8FAFC',
        borderRadius: '5px',
        p: 1.5,
      }}
    >
      <Stack gap={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Make Ready
          </Typography>
          {loading && <CircularProgress size={16} />}
          {!loading && severity && (
            <Chip
              size="small"
              label={severity.label}
              sx={
                untracked
                  ? {
                      bgcolor: '#F1F5F9',
                      color: '#64748B',
                      border: `1px dashed ${severity.hex}`,
                      fontWeight: 600,
                    }
                  : { bgcolor: severity.hex, color: '#fff', fontWeight: 600 }
              }
            />
          )}
        </Stack>

        {!loading && status?.error && (
          <Typography variant="caption" color="text.secondary">
            Make Ready status unavailable right now.
          </Typography>
        )}

        {!loading && status && !status.error && !untracked && (
          <>
            <Typography variant="body2">
              Load list: <strong>{status.loadList?.title || '—'}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {describeCheck(status)}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<ChecklistIcon />}
              onClick={() => navigate(`/make-ready/${assetId}`)}
            >
              Run Make Ready check
            </Button>
            {canManage && !editing && (
              <Button
                size="small"
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => {
                  setSelected(status.loadList?._id || '')
                  setEditing(true)
                }}
              >
                Change load list
              </Button>
            )}
            {canManage && editing && (
              <Stack gap={1}>
                <LoadListSelect
                  kind="asset"
                  loadLists={loadLists}
                  value={selected}
                  onChange={(id) => setSelected(id || '')}
                  disabled={saving}
                />
                <Stack direction="row" gap={1}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={
                      !selected || saving || selected === status.loadList?._id
                    }
                    onClick={() => assign({ runCheck: false })}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    size="small"
                    disabled={saving}
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            )}
          </>
        )}

        {!loading && untracked && (
          <>
            <Typography variant="body2" color="text.secondary">
              No load list assigned yet, so this asset has never had a Make
              Ready check.
            </Typography>
            {canManage ? (
              <>
                <LoadListSelect
                  kind="asset"
                  loadLists={loadLists}
                  value={selected}
                  onChange={(id) => setSelected(id || '')}
                  suggestedLoadList={status.suggestedLoadList}
                  disabled={saving}
                />
                {loadListsForKind(loadLists, 'asset').length > 0 && (
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    startIcon={<PlayArrowIcon />}
                    disabled={!selected || saving}
                    onClick={() => assign({ runCheck: true })}
                  >
                    {saving ? 'Assigning…' : 'Assign and run first check'}
                  </Button>
                )}
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Ask a Fleet, Make Ready or Governance team member to assign a
                load list.
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Box>
  )
}

export default AssetMakeReadyBlock
